import { useId } from "react";

import { FormControl, FormHelperText, FormLabel } from "@mui/material";
import { getFieldError } from "@shared";
import { FormikProps, FormikValues } from "formik";

import { nameof } from "@/common/helper/commonHelper";
import { exchangeSectionLabelSx } from "@/pages/exchange/Exchange.styles";
import { ExchangeRequestedServicesValues } from "@/pages/exchange/Exchange.types";
import { PurposeChipSelect } from "@/pages/exchange/components/purpose-chip-select/PurposeChipSelect";
import { useRequestablePurposes } from "@/pages/exchange/hooks/useRequestablePurposes";
import { useTranslation } from "@/translations";

interface Props<T extends ExchangeRequestedServicesValues & FormikValues> {
  formik: FormikProps<T>;
}

/**
 * The required "Gesuchte Dienste" section of every form that publishes or edits a request: the purposes
 * that can be asked for, as chips. Like the rest of these forms it is only marked after a submit attempt.
 */
export function RequestedPurposesField<T extends ExchangeRequestedServicesValues & FormikValues>({ formik }: Props<T>) {
  const { t } = useTranslation();
  const labelId = useId();
  const errorId = useId();
  const purposes = useRequestablePurposes();

  // Named off the base interface: every T carries this field under the same name.
  const fieldName = nameof<ExchangeRequestedServicesValues>("requestedPurposeKeys");
  const selectedKeys = formik.values.requestedPurposeKeys;
  const error = getFieldError(formik, fieldName);

  const toggle = (key: string) =>
    void formik.setFieldValue(
      fieldName,
      selectedKeys.includes(key) ? selectedKeys.filter((selectedKey) => selectedKey !== key) : [...selectedKeys, key],
      formik.submitCount > 0
    );

  return (
    <FormControl component="fieldset" fullWidth error={error !== undefined}>
      <FormLabel component="legend" required id={labelId} sx={exchangeSectionLabelSx}>
        {t("exchange.requestedServices.section")}
      </FormLabel>
      <PurposeChipSelect
        labelledById={labelId}
        options={purposes.options}
        isLoading={purposes.isLoading}
        selectedKeys={selectedKeys}
        onToggle={toggle}
        hasError={error !== undefined}
        describedById={errorId}
      />
      {error !== undefined && <FormHelperText id={errorId}>{error}</FormHelperText>}
    </FormControl>
  );
}
